import React, { useEffect, useMemo, useState } from 'react';
import {
  BarsArrowDownIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';
import { usePackages } from '../hooks/usePackages';
import type { Package } from './packages/PackageList';
import { formatCurrency } from './SeekerDetailModal/utils';

interface SendPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateAway?: () => void;
  seekerName: string;
  seekerId: string | null;
  onSent?: (offerId: string, selectedPackage: Package) => void;
}

interface PackageSelection extends Package {
  totalUnits: number;
}

const mapPackageTotals = (pkg: Package): PackageSelection => {
  const totalUnits = pkg.components.reduce((sum, component) => sum + component.quantity, 0);
  return { ...pkg, totalUnits };
};

type StripeStatus = {
  connected: boolean;
  status?: {
    fully_setup: boolean;
    charges_enabled: boolean;
    payouts_enabled: boolean;
  };
};

const SendPackageModal: React.FC<SendPackageModalProps> = ({
  isOpen,
  onClose,
  onNavigateAway,
  seekerName,
  seekerId,
  onSent,
}) => {
  const { packages, loading, loadPackages } = usePackages();
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stripe status
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  // Customization state
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [draftPackage, setDraftPackage] = useState<PackageSelection | null>(null);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  // Check if Stripe is fully set up
  const isStripeReady = stripeStatus?.connected && stripeStatus?.status?.fully_setup;

  // Helper to recalculate totals
  const recalcTotals = (pkg: PackageSelection): PackageSelection => {
    const totalUnits = pkg.components.reduce((s, c) => s + c.quantity, 0);
    const totalCost = pkg.components.reduce(
      (s, c) => s + c.unit_price_dollars * c.quantity,
      0,
    );
    return { ...pkg, totalUnits, totalCost };
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedPackageId(null);
      setError(null);
      setIsCustomizing(false);
      setDraftPackage(null);
      setSaveAsTemplate(false);
      setStripeStatus(null);
      setStripeLoading(true);
      return;
    }

    let cancelled = false;
    const hydrate = async () => {
      try {
        // Load packages and Stripe status in parallel
        const supabase = getSupabaseClient();
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          if (!cancelled) {
            setStripeLoading(false);
          }
          return;
        }

        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;

        // Fetch packages
        const packagesPromise = loadPackages();

        // Fetch Stripe status
        const stripePromise = baseUrl
          ? fetch(`${baseUrl}/functions/v1/connect-status`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
            }).then(res => res.ok ? res.json() : null)
          : Promise.resolve(null);

        const [, stripeData] = await Promise.all([packagesPromise, stripePromise]);

        if (!cancelled) {
          setStripeStatus(stripeData);
          setStripeLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load packages';
          setError(message);
          setStripeLoading(false);
        }
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const packageOptions: PackageSelection[] = useMemo(
    () => packages.map(mapPackageTotals),
    [packages]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (packageOptions.length === 0) {
      setSelectedPackageId(null);
      return;
    }
    if (!selectedPackageId || !packageOptions.find((pkg) => pkg.id === selectedPackageId)) {
      setSelectedPackageId(packageOptions[0].id);
    }
  }, [isOpen, packageOptions, selectedPackageId]);

  const selectedPackage = selectedPackageId
    ? packageOptions.find((pkg) => pkg.id === selectedPackageId) ?? null
    : null;

  // Active package is either the draft (when customizing) or the selected template
  const activePackage = isCustomizing && draftPackage ? draftPackage : selectedPackage;

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (sending) return;

    if (!seekerId) {
      setError('This seeker is not yet linked to an account. Package offers can only be sent once onboarding is complete.');
      return;
    }

    const pkgToSend = isCustomizing && draftPackage ? draftPackage : selectedPackage;

    if (!pkgToSend) {
      setError('Choose a package before sending.');
      return;
    }

    // Validate custom package has required fields
    if (isCustomizing && draftPackage) {
      if (!draftPackage.name.trim()) {
        setError('Package name is required.');
        return;
      }
      if (draftPackage.components.length === 0) {
        setError('Add at least one component to the package.');
        return;
      }
      const hasInvalidComponent = draftPackage.components.some(
        c => !c.name.trim() || !c.component_type.trim() || c.quantity <= 0 || c.unit_price_dollars < 0
      );
      if (hasInvalidComponent) {
        setError('All components must have a name, type, quantity > 0, and price >= 0.');
        return;
      }
    }

    setError(null);
    setSending(true);
    try {
      const supabase = getSupabaseClient();
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) {
        throw authError;
      }
      if (!user) {
        throw new Error('You must be signed in to send a package offer.');
      }

      // Build request body based on customization state
      const body =
        isCustomizing && draftPackage
          ? {
              seeker_id: seekerId,
              guide_id: user.id,
              status: 'sent' as const,  // Always send as 'sent', never draft
              save_as_template: saveAsTemplate,
              custom_package: {
                name: draftPackage.name,
                description: draftPackage.description ?? null,
                duration_months: draftPackage.duration_months ?? null,
                components: draftPackage.components.map(c => ({
                  name: c.name,
                  component_type: c.component_type,
                  quantity: c.quantity,
                  unit_price_cents: Math.round(c.unit_price_dollars * 100),
                  session_length_minutes: c.session_length_minutes ?? null,
                })),
              },
            }
          : {
              template_package_id: pkgToSend.id,
              seeker_id: seekerId,
              guide_id: user.id,
              status: 'sent' as const,  // Always send as 'sent', never draft
            };

      const { data: functionData, error: functionError } = await supabase.functions.invoke<{
        offer_id?: string;
        email_sent?: boolean;
        status?: string;
        template_package_id?: string | null;
      }>('send-package-offer', {
        body,
      });

      if (functionError) {
        throw new Error(functionError.message ?? 'Unable to send package offer.');
      }

      const offerId = functionData?.offer_id;
      if (!offerId) {
        throw new Error('Package offer could not be created. Please try again.');
      }

      onSent?.(offerId, pkgToSend);
      setSelectedPackageId(null);
      setIsCustomizing(false);
      setDraftPackage(null);
      setSaveAsTemplate(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send package offer';
      setError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#2C2731]/80 px-4 py-8">
      <div
        className="absolute inset-0 bg-[#2C2731]/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-5xl">
        <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-3xl border border-[#2C2731] bg-[#FFFCEF] shadow-[0_32px_80px_-30px_rgba(32,24,44,0.65)]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              aria-label="Close send package modal"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.png" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>

          <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6 md:flex-row">
            {!stripeLoading && !isStripeReady ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="max-w-md rounded-2xl border border-[#C47C16]/30 bg-[#FFF3D9] px-6 py-6">
                  <div className="flex flex-col items-center gap-4 text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 text-[#C47C16]" />
                    <div>
                      <p className="text-lg font-semibold text-[#C47C16]">Payment Setup Required</p>
                      <p className="text-sm text-[#C47C16]/90 mt-2">
                        You must set up Stripe to receive payments before sending package offers to seekers.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onNavigateAway?.();
                        window.location.hash = 'settings-payments';
                      }}
                      className="inline-block mt-2 px-6 py-3 rounded-full bg-[#C47C16] text-white text-sm font-semibold hover:bg-[#A66812] transition-colors"
                    >
                      Set Up Payments →
                    </button>
                  </div>
                </div>
              </div>
            ) : (
            <>
            <section className="space-y-4 text-[#FFFCEF] md:w-[46%]">
              <div className="rounded-[28px] border border-[#304031]/20 bg-[#FFF8E1]/90 p-5 shadow-[0_30px_60px_-40px_rgba(32,77,60,0.45)]">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">Package offer</p>
                <h2 className="mt-1 font-recoleta text-[28px] font-normal leading-tight text-[#304031]">
                  Send a curated plan
                </h2>
                <p className="mt-2 text-sm text-[#214D3C]">
                  Select the package that best fits <span className="font-semibold ph-mask">{seekerName}</span>. We'll deliver it
                  instantly with your note.
                </p>
                {packageOptions.length > 0 && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#304031]/30 bg-[#FFFCEF] px-3 py-1 text-xs font-semibold text-[#304031]">
                    <BarsArrowDownIcon className="h-4 w-4" />
                    {packageOptions.length} templates
                  </div>
                )}
              </div>

              {error && (
                <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm font-medium text-[#B24E4E]">
                  {error}
                </div>
              )}

              <div className="grid max-h-[420px] gap-3 overflow-y-auto pr-1">
                {loading && packageOptions.length === 0 && (
                  <div className="flex h-40 flex-col items-center justify-center rounded-2xl border border-dashed border-[#304031]/25 bg-[#F9F0C7] text-sm text-[#214D3C]">
                    Loading packages…
                  </div>
                )}

                {!loading && packageOptions.length === 0 && (
                  <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#304031]/25 bg-[#F9F0C7] px-4 text-center text-sm text-[#214D3C]">
                    <CheckCircleIcon className="h-8 w-8 text-[#C7C1AD]" />
                    <div>
                      <p className="font-semibold text-[#304031]">No active packages</p>
                      <p>Create a package first to send customized offers.</p>
                    </div>
                  </div>
                )}

                {!loading &&
                  packageOptions.length > 0 &&
                  packageOptions.map((pkg) => {
                    const isSelected = selectedPackageId === pkg.id;
                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => {
                          // Exit customization mode when selecting a different package
                          if (isCustomizing) {
                            setIsCustomizing(false);
                            setDraftPackage(null);
                            setSaveAsTemplate(false);
                          }
                          setSelectedPackageId(pkg.id);
                        }}
                        className={[
                          'w-full rounded-2xl border px-4 py-4 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFFCEF]',
                          isSelected
                            ? 'border-[#FFFCEF] bg-[#304031] text-[#FFFCEF] shadow-[0_20px_40px_-25px_rgba(32,77,60,0.75)]'
                            : 'border-[#4A3C57]/60 bg-[#2C2731] text-[#FFFCEF] hover:border-[#FFFCEF]/60',
                        ].join(' ')}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-inherit">{pkg.name}</p>
                            <p className="text-xs text-[#F4ECD0]/80">{pkg.description || 'Custom curated plan'}</p>
                          </div>
                          <div className="text-right text-sm font-semibold">
                            {formatCurrency(pkg.totalCost)}
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs text-[#F4ECD0]/80">
                          <span>{pkg.totalUnits} units</span>
                          <span>{pkg.components.length} components</span>
                        </div>
                      </button>
                    );
                  })}

                {!loading && packageOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomizing(true);
                      setDraftPackage({
                        id: `custom-new-${Date.now()}`,
                        name: '',
                        description: '',
                        duration_months: 3,
                        totalCost: 0,
                        totalUnits: 3,
                        components: [
                          {
                            id: `prep-${Date.now()}`,
                            name: 'Preparation Sessions',
                            component_type: 'preparation',
                            quantity: 1,
                            unit_price_dollars: 0,
                            sort_order: 10,
                            session_length_minutes: 60,
                          },
                          {
                            id: `ceremony-${Date.now()}`,
                            name: 'Core Ceremony Sessions',
                            component_type: 'ceremony',
                            quantity: 1,
                            unit_price_dollars: 0,
                            sort_order: 20,
                            session_length_minutes: 60,
                          },
                          {
                            id: `integration-${Date.now()}`,
                            name: 'Integration Sessions',
                            component_type: 'integration',
                            quantity: 1,
                            unit_price_dollars: 0,
                            sort_order: 30,
                            session_length_minutes: 60,
                          },
                        ],
                        is_active: true,
                        created_at: new Date().toISOString(),
                      } as PackageSelection);
                    }}
                    className="w-full rounded-2xl border border-dashed border-[#FFFCEF]/40 bg-[#2C2731]/50 px-4 py-4 text-sm font-medium text-[#FFFCEF] transition hover:border-[#FFFCEF] hover:bg-[#2C2731]"
                  >
                    + Build new one-off package
                  </button>
                )}
              </div>
            </section>

            <section className="flex-1 rounded-2xl border border-[#304031]/15 bg-white/90 p-5 shadow-[0_30px_60px_-40px_rgba(15,8,27,0.45)]">
              {activePackage ? (
                <>
                  {!isCustomizing && selectedPackage && (
                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setIsCustomizing(true);
                          setDraftPackage(
                            JSON.parse(JSON.stringify({
                              ...selectedPackage,
                              id: `custom-${selectedPackage.id}`,
                            }))
                          );
                        }}
                        className="w-full rounded-lg border border-[#304031]/30 bg-[#FFFCEF] px-3 py-2 text-xs font-semibold text-[#304031] transition hover:bg-[#F9F0C7]"
                      >
                        Customize this package for this seeker
                      </button>
                    </div>
                  )}

                  {isCustomizing && (
                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setIsCustomizing(false);
                          setDraftPackage(null);
                          setSaveAsTemplate(false);
                        }}
                        className="text-xs font-medium text-[#214D3C] underline hover:text-[#304031]"
                      >
                        ← Back to template selection
                      </button>
                    </div>
                  )}

                  <div className="mb-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                      {isCustomizing ? 'Customize package' : 'Package summary'}
                    </p>
                    {isCustomizing && draftPackage ? (
                      <div className="mt-2 space-y-3">
                        <input
                          type="text"
                          value={draftPackage.name}
                          onChange={(e) => setDraftPackage({ ...draftPackage, name: e.target.value })}
                          placeholder="Package name"
                          className="w-full rounded-lg border border-[#304031]/30 bg-white px-3 py-2 text-lg font-semibold text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none"
                        />
                        <textarea
                          value={draftPackage.description || ''}
                          onChange={(e) => setDraftPackage({ ...draftPackage, description: e.target.value.slice(0, 500) })}
                          placeholder="Description (optional)"
                          rows={2}
                          maxLength={500}
                          className="w-full rounded-lg border border-[#304031]/30 bg-white px-3 py-2 text-sm text-[#214D3C] placeholder:text-[#214D3C]/40 focus:border-[#304031] focus:outline-none"
                        />
                        <p className={`text-right text-xs ${(draftPackage.description?.length ?? 0) >= 450 ? 'text-[#B24E4E]' : 'text-[#214D3C]/50'}`}>
                          {draftPackage.description?.length ?? 0}/500
                        </p>
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-medium text-[#214D3C]">Duration:</label>
                          <select
                            value={draftPackage.duration_months || ''}
                            onChange={(e) => setDraftPackage({
                              ...draftPackage,
                              duration_months: e.target.value ? parseInt(e.target.value) : null
                            })}
                            className="rounded-lg border border-[#304031]/30 bg-white px-3 py-1 text-sm text-[#304031] focus:border-[#304031] focus:outline-none"
                          >
                            <option value="">Select duration</option>
                            <option value="3">3 months</option>
                            <option value="6">6 months</option>
                            <option value="12">12 months</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="mt-2 text-xl font-semibold text-[#304031]">{activePackage.name}</h3>
                        <p className="text-sm text-[#214D3C]">
                          {activePackage.description || 'A curated offer tailored to this seeker.'}
                        </p>
                        {activePackage.duration_months && (
                          <p className="mt-1 text-xs text-[#214D3C]/70">
                            Duration: {activePackage.duration_months} months
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <div className="rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-4">
                    <div className="flex items-center justify-between text-sm font-semibold text-[#304031]">
                      <span>Total investment</span>
                      <span>{formatCurrency(activePackage.totalCost)}</span>
                    </div>
                    <div className="mt-2 text-xs text-[#214D3C]">{activePackage.totalUnits} total units</div>
                  </div>

                  <div className="mt-5 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">Included components</p>
                    {isCustomizing && draftPackage ? (
                      <div className="space-y-2">
                        {draftPackage.components.map((component, idx) => {
                          const isCoreType = ['preparation', 'ceremony', 'integration'].includes(component.component_type);
                          const isRemovable = component.component_type === 'additional';

                          return (
                            <div
                              key={component.id}
                              className="rounded-xl border border-[#304031]/20 bg-white p-3 space-y-2"
                            >
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={component.name}
                                  onChange={(e) => {
                                    const updated = [...draftPackage.components];
                                    updated[idx] = { ...updated[idx], name: e.target.value };
                                    setDraftPackage(recalcTotals({ ...draftPackage, components: updated }));
                                  }}
                                  placeholder="Component name"
                                  className="flex-1 rounded border border-[#304031]/30 bg-white px-2 py-1 text-sm font-semibold text-[#304031] focus:border-[#304031] focus:outline-none"
                                />
                                {isRemovable && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = draftPackage.components.filter((_, i) => i !== idx);
                                      setDraftPackage(recalcTotals({ ...draftPackage, components: updated }));
                                    }}
                                    className="px-2 text-xs text-[#B24E4E] hover:text-[#8B3A3A]"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-[#214D3C]/70">Type</label>
                                  <input
                                    type="text"
                                    value={component.component_type}
                                    disabled
                                    className="w-full rounded border border-[#304031]/30 bg-[#F9F0C7]/50 px-2 py-1 text-xs text-[#214D3C]/70 cursor-not-allowed"
                                  />
                                </div>
                              <div>
                                <label className="text-xs text-[#214D3C]/70">Quantity</label>
                                <input
                                  type="number"
                                  value={component.quantity}
                                  onChange={(e) => {
                                    const updated = [...draftPackage.components];
                                    updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value) || 0 };
                                    setDraftPackage(recalcTotals({ ...draftPackage, components: updated }));
                                  }}
                                  min="1"
                                  className="w-full rounded border border-[#304031]/30 bg-white px-2 py-1 text-xs text-[#214D3C] focus:border-[#304031] focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-[#214D3C]/70">Price ($)</label>
                                <input
                                  type="number"
                                  value={component.unit_price_dollars}
                                  onChange={(e) => {
                                    const updated = [...draftPackage.components];
                                    updated[idx] = { ...updated[idx], unit_price_dollars: parseFloat(e.target.value) || 0 };
                                    setDraftPackage(recalcTotals({ ...draftPackage, components: updated }));
                                  }}
                                  min="0"
                                  step="0.01"
                                  className="w-full rounded border border-[#304031]/30 bg-white px-2 py-1 text-xs text-[#214D3C] focus:border-[#304031] focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-[#214D3C]/70">Session (min)</label>
                                <input
                                  type="number"
                                  value={component.session_length_minutes ?? 60}
                                  onChange={(e) => {
                                    const updated = [...draftPackage.components];
                                    updated[idx] = {
                                      ...updated[idx],
                                      session_length_minutes: e.target.value ? parseInt(e.target.value) : 60
                                    };
                                    setDraftPackage({ ...draftPackage, components: updated });
                                  }}
                                  min="1"
                                  className="w-full rounded border border-[#304031]/30 bg-white px-2 py-1 text-xs text-[#214D3C] focus:border-[#304031] focus:outline-none"
                                />
                              </div>
                            </div>
                          </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => {
                            const newComponent = {
                              id: `new-${Date.now()}`,
                              name: '',
                              component_type: 'additional',
                              quantity: 1,
                              unit_price_dollars: 0,
                              sort_order: draftPackage.components.length * 10 + 10,
                              session_length_minutes: 60,
                            };
                            setDraftPackage({
                              ...draftPackage,
                              components: [...draftPackage.components, newComponent]
                            });
                          }}
                          className="w-full rounded-lg border border-dashed border-[#304031]/30 bg-[#FFFCEF]/50 px-4 py-3 text-sm font-medium text-[#304031] transition hover:bg-[#FFFCEF]"
                        >
                          + Add additional component
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {activePackage.components.map((component) => (
                          <div
                            key={component.id}
                            className="flex items-center justify-between rounded-xl border border-[#304031]/10 bg-white px-4 py-3 text-sm text-[#214D3C]"
                          >
                            <div>
                              <p className="font-semibold text-[#304031]">{component.name}</p>
                              <p className="text-xs uppercase tracking-[0.1em] text-[#214D3C]/70">
                                {component.component_type}
                                {component.session_length_minutes && (
                                  <span className="ml-2">• {component.session_length_minutes} min</span>
                                )}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-[#304031]">
                                {formatCurrency(component.unit_price_dollars)}
                              </p>
                              <p className="text-xs text-[#214D3C]/70">{component.quantity} units</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-[#214D3C]">
                  {loading ? 'Loading package details…' : 'Select a package to review the details.'}
                </div>
              )}
            </section>
            </>
            )}
          </main>

          {isStripeReady && (
          <footer className="flex flex-col gap-3 border-t border-[#FFFCEF]/25 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-6 pt-4">
            {isCustomizing && (
              <label className="flex items-center gap-2 text-sm text-[#304031]">
                <input
                  type="checkbox"
                  checked={saveAsTemplate}
                  onChange={(e) => setSaveAsTemplate(e.target.checked)}
                  className="h-4 w-4 rounded border-[#304031]/30 text-[#304031] focus:ring-[#304031]"
                />
                <span>Save this configuration as a reusable template</span>
              </label>
            )}
            <div className="flex flex-col gap-3 md:flex-row">
              <button
                type="button"
                onClick={onClose}
                className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#304031]/40 bg-[#FFFCEF] px-6 py-4 text-base font-medium text-[#304031] transition hover:-translate-y-0.5 hover:border-[#304031]"
              >
                <span className="w-5 text-xl leading-none">{'\u2190'}</span>
                <span>Cancel</span>
                <span className="invisible w-5 text-xl leading-none">{'\u2192'}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!activePackage || sending}
                className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#304031] bg-[#304031] px-6 py-4 text-base font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
              >
                <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
                <span>{sending ? 'Sending…' : 'Send package offer'}</span>
                <span className="w-5 text-xl leading-none">{'\u2192'}</span>
              </button>
            </div>
          </footer>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendPackageModal;
